import * as Yup from 'yup';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Link, Stack, Alert, IconButton, InputAdornment, Box } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useDispatch } from 'react-redux';
// routes
import { PATH_AUTH } from '../../../routes/paths';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
// components
import Iconify from '../../../components/Iconify';
import { FormProvider, RHFTextField, RHFCheckbox } from '../../../components/hook-form';
import { closeModal } from '../../../redux/slices/authModal';

// ----------------------------------------------------------------------

export default function LoginForm({ kiosk }) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isMountedRef = useIsMountedRef();

  const [showPassword, setShowPassword] = useState(false);

  const LoginSchema = Yup.object().shape({
    email: Yup.string().email('Email must be a valid email address').required('Email is required'),
    password: Yup.string().required('Password is required'),
  });

  // const defaultValues = {
  //   email: 'demo@minimals.cc',
  //   password: 'demo1234',
  //   remember: true,
  // };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
  });

  const {
    reset,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;

  const onSubmit = async (data) => {
    try {
      const emailLowercase = data.email.toLowerCase();
      await login(emailLowercase, data.password);
      navigate('/auth/crossing');
      reset();
      // errors.afterSubmit = null;
      dispatch(closeModal());
    } catch (error) {
      console.error(error);

      if (isMountedRef.current) {
        setError('afterSubmit', { ...error, message: error.message });
      }
    }
  };

  const kioskStyles = kiosk
    ? {
        formContainer: {
          width: '100%',
          maxWidth: '600px',
          margin: '0 auto',
          padding: '24px',
        },
        inputField: {
          '& .MuiInputBase-input': {
            fontSize: '1.2rem',
            padding: '16px 14px',
          },
          '& .MuiInputLabel-root': {
            fontSize: '1.2rem',
          },
        },
        submitButton: {
          height: '60px',
          fontSize: '1.2rem',
          fontWeight: 'bold',
        },
      }
    : {};

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={4} sx={kioskStyles.formContainer}>
        {!!errors.afterSubmit && (
          <Alert severity="error" sx={kiosk ? { fontSize: '1.1rem' } : {}}>
            {errors.afterSubmit.detail
              ? errors.afterSubmit.detail
              : 'There was an issue while trying to establish connection. Please contact support.'}
          </Alert>
        )}

        <RHFTextField name="email" label={kiosk ? 'Kiosk ID' : 'Email address'} sx={kioskStyles.inputField} />

        <RHFTextField
          name="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          sx={kioskStyles.inputField}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  sx={kiosk ? { fontSize: '1.5rem' } : {}}
                >
                  <Iconify
                    icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'}
                    width={kiosk ? 28 : 24}
                    height={kiosk ? 28 : 24}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {!kiosk && (
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }}>
            <RHFCheckbox name="remember" label="Remember me" />
            <Link component={RouterLink} variant="subtitle2" to={PATH_AUTH.resetPassword}>
              Forgot password?
            </Link>
          </Stack>
        )}

        {kiosk && <Box sx={{ mt: 3 }} />}

        <LoadingButton
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          loading={isSubmitting}
          sx={kioskStyles.submitButton}
        >
          Login
        </LoadingButton>
      </Stack>
    </FormProvider>
  );
}
